import { useState } from 'react';
import { Alert, Button, Checkbox, Code, Group, Stack, Text } from '@mantine/core';
import {
  useAutomationActivation,
  useActivationHistory,
  useLegacyActivationAudit,
  useDirectRestoreConfirmation,
  usePublicationDocuments,
  usePublicationIntegrity,
  useRestorePreviousLiveState,
} from '@/hooks/api/automations/useAutomationPublications.ts';
import PublicationPanel from './PublicationPanel.tsx';
import ReleaseDocumentationReview from './ReleaseDocumentationReview.tsx';
import { useCompositionScope } from '@/hooks/api/automations/useExecutionCompositions.ts';
import {
  ExactDetails,
  ImplementationLabel,
  ReleaseSection,
  TriggerLabel,
} from '@/pages/Editor/components/Composition/CompositionPresentation.tsx';
import type {
  ActivationHistoryItem,
  DirectRestorePublicationPreview,
} from '@/types/automationPublications.ts';
type Props = {
  automationId: string;
  canEdit: boolean;
};
export default function PublicationHistory(props: Props) {
  const scope = useCompositionScope();
  return <HistorySession key={JSON.stringify([scope, props.automationId])} {...props} />;
}

function HistorySession({ automationId, canEdit }: Props) {
  const current = useAutomationActivation(automationId);
  const integrity = usePublicationIntegrity(automationId);
  const [before, setBefore] = useState<number>();
  const history = useActivationHistory(automationId, before);
  const [rollback, setRollback] = useState(false);
  const [directRestoreReview, setDirectRestoreReview] = useState(false);
  const [directRestorePreview, setDirectRestorePreview] =
    useState<DirectRestorePublicationPreview | null>(null);
  const [directRestoreRequestId, setDirectRestoreRequestId] = useState<string | null>(null);
  const [directRestoreDocumentsOpen, setDirectRestoreDocumentsOpen] = useState(false);
  const [directRestoreDocumentsAcknowledged, setDirectRestoreDocumentsAcknowledged] =
    useState(false);
  const restorePreview = useRestorePreviousLiveState();
  const confirm = useDirectRestoreConfirmation();
  const audit = useLegacyActivationAudit(automationId, current.isError);
  const activation = current.isFetchedAfterMount && !current.isError ? current.data : undefined;
  const events = history.isFetchedAfterMount && !history.isError ? history.data : undefined;
  const legacyAudit = audit.isFetchedAfterMount && !audit.isError ? audit.data : undefined;
  const health = integrity.isFetchedAfterMount && !integrity.isError ? integrity.data : undefined;
  const publicationChanged = Boolean(
    health &&
    activation &&
    health.status === 'healthy' &&
    (health.head_revision !== activation.head_revision ||
      health.published_version_id !== activation.published_version_id ||
      health.published_release_id !== activation.published_release_id ||
      !health.published_version_id ||
      activation.active_roots.some(
        (root) => root.automation_version_id !== health.published_version_id,
      )),
  );
  const rollbackAvailable =
    current.isFetchedAfterMount &&
    !current.isFetching &&
    !current.isError &&
    integrity.isFetchedAfterMount &&
    !integrity.isFetching &&
    !integrity.isError &&
    health?.status === 'healthy' &&
    health.head_revision === activation?.head_revision &&
    health.published_version_id === activation?.published_version_id &&
    health.published_release_id === activation?.published_release_id &&
    Boolean(health.published_version_id) &&
    Boolean(activation?.active_roots.length) &&
    activation?.active_roots.every(
      (root) => root.automation_version_id === health.published_version_id,
    );
  const activeEvent = events?.find((event) => event.head_revision === activation?.head_revision);
  const restoreDocumentFences = [
    ...new Map(
      (directRestorePreview?.plan.targets ?? [])
        .flatMap((target) => (target.selected_release ? [target.selected_release] : []))
        .map((fence) => [fence.release_id, fence] as const),
    ).values(),
  ];
  const restoreDocuments = usePublicationDocuments(
    restoreDocumentFences,
    directRestoreDocumentsOpen,
  );
  const restoreDocumentsReady =
    restoreDocumentFences.length > 0 &&
    restoreDocuments.every(
      (result) =>
        result.data && !result.isError && !result.isFetching && result.isFetchedAfterMount,
    );
  const directRestoreAvailable =
    rollbackAvailable &&
    activation?.active_target_kind === 'version' &&
    Boolean(activeEvent?.previous_activation_id);
  return (
    <ReleaseSection
      title="Release activity"
      description="Publishing and restore history for this automation."
    >
      {(integrity.isError ||
        publicationChanged ||
        (health && !['healthy', 'unpublished', 'rolled_back'].includes(health.status))) && (
        <Alert color="red" title="Active publication needs attention">
          {publicationChanged
            ? 'Publication changed. Refresh its selection and health before reviewing rollback.'
            : (health?.findings[0]?.message ?? 'Publication integrity could not be verified.')}
          <Button
            variant="subtle"
            onClick={() => void Promise.all([current.refetch(), integrity.refetch()])}
          >
            Refresh publication health
          </Button>
        </Alert>
      )}
      {current.isError && (
        <Alert color="orange">
          Current activation is unavailable.{' '}
          {legacyAudit?.status === 'eligible'
            ? 'Legacy publication requires operator adoption before operational use; no assurance is inferred.'
            : (legacyAudit?.finding ??
              'Refresh or ask an authorized operator to inspect this selection.')}
          <Button
            variant="subtle"
            onClick={() => {
              void current.refetch();
              void audit.refetch();
            }}
          >
            Refresh activation
          </Button>
        </Alert>
      )}
      {activation && (
        <Stack gap={2}>
          <Text>
            {activation.active_target_kind === 'release'
              ? 'A Release is currently published.'
              : activation.active_target_kind === 'version'
                ? 'A version is currently published.'
                : 'This Automation is unpublished.'}
          </Text>
          <details>
            <summary>Technical publication details</summary>
            <Code>
              {activation.active_target_kind === 'release'
                ? (activation.published_release_id ?? 'unpublished')
                : (activation.published_version_id ?? 'unpublished')}
            </Code>{' '}
            · head revision {activation.head_revision}
          </details>
        </Stack>
      )}
      {history.isError && (
        <Alert color="red">
          History unavailable.{' '}
          <Button variant="subtle" onClick={() => void history.refetch()}>
            Refresh history
          </Button>
        </Alert>
      )}
      {!history.isError && !history.isFetchedAfterMount && (
        <Text>Loading publication history…</Text>
      )}
      {events?.length === 0 && (
        <Text>No publication events are available in this access scope.</Text>
      )}
      {events?.map((event) => (
        <HistoryEvent key={event.activation_id} event={event} />
      ))}
      {events?.length === 100 && (
        <Button variant="default" onClick={() => setBefore(events[99].head_revision)}>
          Older publication events
        </Button>
      )}
      {before !== undefined && (
        <Button variant="subtle" onClick={() => setBefore(undefined)}>
          Newest publication events
        </Button>
      )}
      {canEdit && activation?.published_release_id && (
        <Button
          variant="default"
          disabled={current.isFetching || !rollbackAvailable}
          onClick={() => setRollback((value) => !value)}
          aria-label={rollback ? 'Close restore review' : 'Review restore of previous live state'}
        >
          {rollback ? 'Close restore review' : 'Restore previous live state'}
        </Button>
      )}
      {canEdit && directRestoreAvailable && activation && (
        <>
          <Button
            variant="default"
            disabled={restorePreview.isPending || confirm.isPending}
            onClick={() => {
              restorePreview.reset();
              confirm.reset();
              setDirectRestoreReview(true);
              setDirectRestorePreview(null);
              setDirectRestoreRequestId(null);
              void restorePreview
                .mutateAsync({
                  automationId,
                  expectedHeadRevision: activation.head_revision,
                })
                .then((preview) => {
                  setDirectRestorePreview(preview);
                  setDirectRestoreRequestId(crypto.randomUUID());
                })
                .catch(() => setDirectRestorePreview(null));
            }}
          >
            Restore previous live state
          </Button>
          {directRestoreReview && (
            <Stack gap="xs">
              {restorePreview.isPending && <Text>Preparing restore review…</Text>}
              {restorePreview.isError && (
                <Alert color="red">
                  The restore review could not be prepared. Refresh and try again.
                </Alert>
              )}
              {directRestorePreview && (
                <>
                  <Text>
                    This restores the exact immediately preceding live state. It does not change
                    version history or create a new draft.
                  </Text>
                  {confirm.isError && (
                    <Alert color="red">
                      The restore result was not confirmed. Retry confirmation with the same
                      reviewed request.
                    </Alert>
                  )}
                  <Group justify="flex-end">
                    <Button
                      variant="default"
                      disabled={confirm.isPending}
                      onClick={() => {
                        restorePreview.reset();
                        confirm.reset();
                        setDirectRestoreReview(false);
                        setDirectRestorePreview(null);
                        setDirectRestoreRequestId(null);
                        setDirectRestoreDocumentsOpen(false);
                        setDirectRestoreDocumentsAcknowledged(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={
                        directRestorePreview.plan.document_confirmation_required &&
                        (!directRestoreDocumentsOpen ||
                          !restoreDocumentsReady ||
                          !directRestoreDocumentsAcknowledged)
                      }
                      loading={confirm.isPending}
                      onClick={() =>
                        directRestoreRequestId &&
                        void confirm
                          .mutateAsync({
                            schema_version: 'automation_activation_apply.v1',
                            request_id: directRestoreRequestId,
                            preview_token: directRestorePreview.preview_token,
                            preview_digest: directRestorePreview.preview_digest,
                            confirm_documentation:
                              directRestorePreview.plan.document_confirmation_required,
                          })
                          .then(() => {
                            confirm.reset();
                            setDirectRestoreReview(false);
                            setDirectRestorePreview(null);
                            setDirectRestoreRequestId(null);
                            setDirectRestoreDocumentsOpen(false);
                            setDirectRestoreDocumentsAcknowledged(false);
                            void Promise.all([
                              current.refetch(),
                              integrity.refetch(),
                              history.refetch(),
                            ]);
                          })
                          .catch(() => undefined)
                      }
                    >
                      Confirm restore
                    </Button>
                  </Group>
                  {restoreDocumentFences.length > 0 && !directRestoreDocumentsOpen && (
                    <Button variant="default" onClick={() => setDirectRestoreDocumentsOpen(true)}>
                      Read exact selected documents
                    </Button>
                  )}
                  {directRestoreDocumentsOpen &&
                    restoreDocumentFences.map((fence, index) => {
                      const result = restoreDocuments[index];
                      if (!result) return null;
                      const documents = result.data;
                      if (documents)
                        return (
                          <ReleaseDocumentationReview
                            key={fence.release_id}
                            releaseId={fence.release_id}
                            documents={documents}
                            detail={documents}
                            showDocuments
                            loadingDocuments={result.isFetching}
                            documentsError={result.error}
                            onToggleDocuments={() =>
                              setDirectRestoreDocumentsOpen((value) => !value)
                            }
                            onRetryDocuments={() => void result.refetch()}
                            compact
                          />
                        );
                      if (!result.isError) return null;
                      return (
                        <Alert
                          key={fence.release_id}
                          color="red"
                          title="Exact Release documents unavailable"
                        >
                          The documents for this restore could not be loaded.
                          <Button
                            variant="subtle"
                            disabled={result.isFetching}
                            onClick={() => void result.refetch()}
                          >
                            Retry
                          </Button>
                        </Alert>
                      );
                    })}
                  {directRestorePreview.plan.document_confirmation_required && (
                    <Checkbox
                      checked={directRestoreDocumentsAcknowledged}
                      disabled={!restoreDocumentsReady}
                      label="I reviewed the exact Release documents."
                      onChange={(event) =>
                        setDirectRestoreDocumentsAcknowledged(event.currentTarget.checked)
                      }
                    />
                  )}
                </>
              )}
            </Stack>
          )}
        </>
      )}
      {rollback && rollbackAvailable && activation?.published_release_id && (
        <PublicationPanel
          releaseId={activation.published_release_id}
          canEdit={canEdit}
          automationIds={[automationId]}
          action="rollback"
          onCompleted={() => {
            setRollback(false);
            void current.refetch();
            void integrity.refetch();
            void history.refetch();
          }}
        />
      )}
    </ReleaseSection>
  );
}

function HistoryEvent({ event }: { event: ActivationHistoryItem }) {
  // Older API responses predate target_kind. Preserve their readable history
  // without overriding an explicit null from the current contract.
  const targetKind =
    event.target_kind === undefined
      ? event.release_id
        ? 'release'
        : event.automation_version_id
          ? 'version'
          : null
      : event.target_kind;
  return (
    <Stack gap={2}>
      <Text>
        {
          {
            activate: 'Published',
            rollback: 'Restored',
            backfill: 'Legacy publication adopted',
          }[event.action]
        }
        {' · '}
        {new Date(event.created_at).toLocaleString()}
      </Text>
      <HistoryRootLabels roots={event.roots} />
      <ExactDetails
        summary="Publication event details"
        values={{
          Target:
            targetKind === 'release'
              ? 'Release'
              : targetKind === 'version'
                ? 'Direct version'
                : 'Unpublished',
          'Release ID': event.release_id,
          'Version ID': event.automation_version_id,
          'Publication ID': event.publication_id,
          'Activation ID': event.activation_id,
          Revision: String(event.head_revision),
          Actor: `${event.actor_type} ${event.actor_id}`,
          ...Object.fromEntries(
            event.roots.flatMap((root) => [
              [`Binding (${root.trigger_step_id})`, root.binding_id],
              [`Automation (${root.trigger_step_id})`, root.automation_id],
              [`Version (${root.trigger_step_id})`, root.automation_version_id],
              [`Trigger (${root.trigger_step_id})`, root.trigger_step_id],
            ]),
          ),
        }}
      />
    </Stack>
  );
}

/** Historical labels are optional reads; opening one event does not hydrate the whole page. */
function HistoryRootLabels({ roots }: { roots: ActivationHistoryItem['roots'] }) {
  const [open, setOpen] = useState(false);
  return (
    <details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>Root names and triggers</summary>
      {open &&
        roots.map((root) => (
          <Text key={root.trigger_step_id} size="xs">
            <ImplementationLabel version={root} />
            {' · '}
            <TriggerLabel versionId={root.automation_version_id} triggerId={root.trigger_step_id} />
          </Text>
        ))}
    </details>
  );
}
